import { useState } from "react";
import { usePrefersReducedMotion } from "../../hooks/usePrefersReducedMotion";
import { observer } from "mobx-react-lite";
import { motion } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../hooks";
import { validators } from "../../utils/validators.util";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import { AuthPageChrome, AUTH_FORM_CARD_CLASS } from "../../components/auth/AuthPageChrome";
import {
  Brain,
  Mail,
  Lock,
  Eye,
  EyeOff,
  User,
  GraduationCap,
  Zap,
  Target,
  TrendingUp,
  Sparkles,
  Shield,
} from "lucide-react";
import type { SignUpData } from "../../types";
import mascoteAnotando from "../../assets/mascote3d/MascoteAnotando.svg";

export type SignUpMode = "student" | "teacher";

type SignUpProps = {
  mode?: SignUpMode;
};

const inputClass =
  "h-12 rounded-lg border-white/15 bg-white/[0.05] pl-10 text-white placeholder:text-slate-500 ring-1 ring-inset ring-white/10 focus-visible:border-teal-500/50 focus-visible:ring-teal-500/25";

const SignUp = observer(function SignUp({ mode = "student" }: SignUpProps) {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const isTeacher = mode === "teacher";
  const reducedMotion = usePrefersReducedMotion();

  const [formData, setFormData] = useState<SignUpData>({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    userType: isTeacher ? "teacher" : "student",
    institutionCode: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!validators.name(formData.name)) {
      newErrors.name = "Nome deve ter entre 3 e 100 caracteres";
    }

    if (!validators.email(formData.email)) {
      newErrors.email = "E-mail inválido";
    }

    if (!validators.password(formData.password)) {
      newErrors.password = "Senha deve ter pelo menos 8 caracteres";
    }

    if (!validators.passwordsMatch(formData.password, formData.confirmPassword)) {
      newErrors.confirmPassword = "As senhas não coincidem";
    }

    if (!acceptPrivacy) {
      newErrors.acceptPrivacy = "Aceite a política de privacidade para continuar.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const payload: SignUpData = {
        ...formData,
        userType: isTeacher ? "teacher" : "student",
        institutionCode: undefined,
      };
      await signUp(payload);
      navigate(isTeacher ? "/dashboard" : "/onboarding");
    } catch (error) {
      setErrors({
        submit: error instanceof Error ? error.message : "Erro ao criar conta. Tente novamente.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const studentBenefits = [
    { icon: Brain, text: "Questionário vira perfil estruturado — alimenta apresentação e ritmo, não é decorativo." },
    { icon: Zap, text: "Mesmo conteúdo base, com ênfase visual, textual ou guiada conforme o seu perfil." },
    { icon: Target, text: "Plano de estudo, revisão e gamificação separados da nota formal." },
    { icon: TrendingUp, text: "Evolução rastreada por sessão — você vê exatamente onde está no seu progresso." },
  ];

  const teacherBenefits = [
    { icon: GraduationCap, text: "Conta professor para integração com salas, módulos e turmas (roadmap de implantação)." },
    { icon: Shield, text: "Camada avaliativa e notas separadas de pontos e rankings de engajamento." },
    { icon: Target, text: "Atividades, prazos e correção no mesmo ecossistema da personalização do aluno." },
    { icon: Brain, text: "Alinhado ao modelo de papéis do produto: professor, coordenação e admin distintos." },
  ];

  const benefits = isTeacher ? teacherBenefits : studentBenefits;

  return (
    <AuthPageChrome>
      <div className="relative z-10 container mx-auto max-w-7xl px-4 pb-12 pt-6 sm:px-6 sm:pb-16 sm:pt-8">
        <div className="grid items-start gap-10 lg:grid-cols-[1.02fr_0.98fr] lg:gap-14">
          <div className="hidden min-w-0 space-y-6 lg:block">

            {/* Headline + mascote lado a lado */}
            <div className="flex items-end gap-4">
              <div className="min-w-0 flex-1 space-y-4">
                <Badge className="border border-teal-400/30 bg-teal-500/10 px-3 py-1 text-xs text-teal-200 sm:text-sm">
                  <Sparkles className="mr-1.5 inline h-3.5 w-3.5" />
                  {isTeacher
                    ? "Papel professor · integração escolar"
                    : "Estudante · personalização por perfil"}
                </Badge>

                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-teal-400 to-cyan-500 shadow-lg shadow-teal-500/25 sm:h-12 sm:w-12">
                    <Brain className="h-5 w-5 text-white sm:h-6 sm:w-6" />
                  </div>
                  <div>
                    <p className="text-lg font-semibold tracking-tight">NeuroMent</p>
                    <p className="text-sm text-teal-300/90">Personalização por perfil · gestão institucional</p>
                  </div>
                </div>

                <h1 className="text-3xl font-semibold leading-[1.12] tracking-tight text-white lg:text-4xl lg:leading-[1.1]">
                  {isTeacher ? (
                    <>
                      Conta{" "}
                      <span className="bg-gradient-to-r from-teal-300 via-cyan-200 to-sky-300 bg-clip-text text-transparent">
                        professor
                      </span>
                    </>
                  ) : (
                    <>
                      Crie a sua conta{" "}
                      <span className="bg-gradient-to-r from-teal-300 via-cyan-200 to-sky-300 bg-clip-text text-transparent">
                        de estudante
                      </span>
                    </>
                  )}
                </h1>
              </div>

              {/* Mascote — ao lado da headline */}
              <div className="relative shrink-0">
                <div
                  aria-hidden
                  className="pointer-events-none absolute -bottom-4 left-1/2 h-20 w-36 -translate-x-1/2"
                  style={{
                    background: "radial-gradient(ellipse, rgba(45,212,191,0.30) 0%, transparent 70%)",
                    filter: "blur(22px)",
                  }}
                />
                <motion.img
                  src={mascoteAnotando}
                  alt=""
                  aria-hidden
                  loading="lazy"
                  decoding="async"
                  fetchPriority="low"
                  className="relative h-52 w-auto select-none will-change-transform drop-shadow-[0_20px_40px_rgba(45,212,191,0.25)]"
                  animate={reducedMotion ? {} : { y: [0, -8, 0] }}
                  transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
                  draggable={false}
                />
              </div>
            </div>

            <p className="max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
              {isTeacher ? (
                <>
                  Para <strong className="font-medium text-slate-200">professores</strong> com convite ou provisionamento pela
                  escola. O questionário de personalização do produto é focado no aprendiz — o seu onboarding principal é
                  operacional (salas e turmas conforme roadmap).
                </>
              ) : (
                <>
                  Depois do cadastro você responde o questionário de perfil —{" "}
                  <strong className="font-medium text-slate-200">as respostas alimentam a forma como o conteúdo é apresentado</strong>{" "}
                  para você.
                </>
              )}
            </p>

            <ul className="space-y-3 text-sm text-slate-300">
              {benefits.map((benefit, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/5 ring-1 ring-white/10">
                    <benefit.icon className="h-4 w-4 text-teal-300" />
                  </span>
                  <span className="min-w-0 pt-0.5 leading-snug">{benefit.text}</span>
                </li>
              ))}
            </ul>
          </div>

          <Card className={AUTH_FORM_CARD_CLASS}>
            <div
              aria-hidden
              className="pointer-events-none absolute inset-x-10 top-0 h-px bg-gradient-to-r from-transparent via-teal-400/45 to-transparent"
            />
            <div className="relative space-y-6">
              <div className="text-center lg:text-left">
                <Badge className="mb-3 border border-white/15 bg-white/[0.06] text-slate-200 lg:hidden">
                  {isTeacher ? "Professor" : "Estudante"}
                </Badge>
                <h2 className="text-xl font-semibold tracking-tight text-white sm:text-2xl">
                  {isTeacher ? "Cadastro de professor" : "Cadastro de estudante"}
                </h2>
                {isTeacher && (
                  <p className="mt-2 text-sm leading-relaxed text-slate-400 sm:text-base">
                    Use as credenciais acordadas com a sua instituição. Depois do registro, o dashboard pedagógico será expandido conforme o rollout.
                  </p>
                )}
                {!isTeacher && (
                  <p className="mt-4 text-sm">
                    <Link
                      to="/cadastro-instituicao"
                      className="text-teal-400 underline-offset-2 hover:text-teal-300 hover:underline"
                    >
                      Cadastro institucional (escola / rede)
                    </Link>
                  </p>
                )}
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-slate-200">
                    Nome completo
                  </Label>
                  <div className="relative">
                    <User className="pointer-events-none absolute left-3 top-1/2 z-10 h-5 w-5 -translate-y-1/2 text-slate-500" />
                    <Input
                      id="name"
                      type="text"
                      placeholder="Seu nome (usado como identificador na API)"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className={inputClass}
                      required
                    />
                  </div>
                  {errors.name && <p className="text-sm text-red-400">{errors.name}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-200">
                    E-mail
                  </Label>
                  <div className="relative">
                    <Mail className="pointer-events-none absolute left-3 top-1/2 z-10 h-5 w-5 -translate-y-1/2 text-slate-500" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="seu@email.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className={inputClass}
                      required
                    />
                  </div>
                  {errors.email && <p className="text-sm text-red-400">{errors.email}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-200">
                    Senha
                  </Label>
                  <div className="relative">
                    <Lock className="pointer-events-none absolute left-3 top-1/2 z-10 h-5 w-5 -translate-y-1/2 text-slate-500" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className={`${inputClass} pr-10`}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 z-10 -translate-y-1/2 text-slate-500 hover:text-slate-200"
                      aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  {errors.password && <p className="text-sm text-red-400">{errors.password}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-slate-200">
                    Confirmar senha
                  </Label>
                  <div className="relative">
                    <Lock className="pointer-events-none absolute left-3 top-1/2 z-10 h-5 w-5 -translate-y-1/2 text-slate-500" />
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      className={`${inputClass} pr-10`}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 z-10 -translate-y-1/2 text-slate-500 hover:text-slate-200"
                      aria-label={showConfirmPassword ? "Ocultar confirmação" : "Mostrar confirmação"}
                    >
                      {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  {errors.confirmPassword && <p className="text-sm text-red-400">{errors.confirmPassword}</p>}
                </div>

                <div className={`flex items-start gap-3 rounded-xl border p-3.5 transition-colors duration-200 ${
                  errors.acceptPrivacy ? "border-red-500/35 bg-red-500/[0.04]" : "border-white/[0.08] bg-white/[0.02]"
                }`}>
                  <Checkbox
                    id="privacy"
                    checked={acceptPrivacy}
                    onCheckedChange={(c) => setAcceptPrivacy(c === true)}
                    className="mt-0.5 shrink-0 rounded-md border-white/25 bg-white/5 data-[state=checked]:border-teal-500 data-[state=checked]:bg-teal-600"
                  />
                  <div className="min-w-0">
                    <label htmlFor="privacy" className="cursor-pointer text-sm leading-relaxed text-slate-400">
                      Li e aceito a{" "}
                      <span className="text-teal-400/80">política de privacidade</span>{" "}
                      do NeuroMent.
                    </label>
                    {errors.acceptPrivacy && (
                      <p className="mt-1 text-xs text-red-400">{errors.acceptPrivacy}</p>
                    )}
                  </div>
                </div>

                {errors.submit && (
                  <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-300">
                    {errors.submit}
                  </div>
                )}

                <Button
                  type="submit"
                  size="lg"
                  className="landing-cta-glow h-12 w-full rounded-lg bg-gradient-to-r from-teal-500 to-cyan-600 text-sm font-semibold shadow-xl shadow-teal-950/50 hover:from-teal-400 hover:to-cyan-500 sm:h-14 sm:text-base"
                  disabled={isLoading}
                >
                  {isLoading ? "Criando conta…" : "Criar conta"}
                </Button>
              </form>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/10" />
                </div>
                <div className="relative flex justify-center text-xs sm:text-sm">
                  <span className="rounded-md bg-white/[0.08] px-3 py-0.5 text-slate-500 ring-1 ring-inset ring-white/5">
                    Já tem conta?
                  </span>
                </div>
              </div>

              <Button
                type="button"
                onClick={() => navigate("/login")}
                variant="ghost"
                size="lg"
                className="h-12 w-full rounded-lg border-0 bg-white/5 text-white ring-1 ring-inset ring-white/20 hover:bg-white/10 hover:ring-white/35 sm:h-14"
              >
                Entrar
              </Button>

            </div>
          </Card>
        </div>
      </div>
    </AuthPageChrome>
  );
});

export default SignUp;
